import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Clock, User, CheckCircle, Calendar, Loader2, Check, X, ExternalLink, DollarSign, ChevronDown, ChevronRight, XCircle, Zap, PenTool } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { apiClient } from '@/services/api';
import { debugLog, errorLog } from '@/utils/logger';
import { captureEvent } from '@/utils/posthog';

interface PayoutRequest {
  id: string;
  userId: string;
  totalHours: number;
  totalCoins: number;
  status: 'pending' | 'approved' | 'paid' | 'rejected';
  adminId?: string;
  processedAt?: string;
  rejectionReason?: string;
  approvalMethod?: 'auto' | 'manual';
  approvalReason?: string;
  signatureRequestId?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    fullName: string;
    email: string;
    walletAddress?: string;
  };
  sessions?: WorkSession[];
  reward?: {
    id: string;
    status: string;
    transactionHash?: string;
    confirmationsReceived?: number;
  };
}

interface WorkSession {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  durationMinutes: number;
  description?: string;
}

interface RewardStatus {
  requestId: string;
  status: 'confirming' | 'sent' | 'failed';
  transactionHash: string;
  coinsEarned: number;
  confirmations?: {
    current: number;
    required: number;
    progress: number;
  };
  error?: string;
}

const PayoutManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<PayoutRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [confirmingRewards, setConfirmingRewards] = useState<Map<string, RewardStatus>>(new Map());
  const [approvalResults, setApprovalResults] = useState<Map<string, { autoApproved: boolean; reason?: string }>>(new Map());

  useEffect(() => {
    fetchRequests();
  }, []);

  // Polling effect for confirming rewards
  useEffect(() => {
    if (confirmingRewards.size === 0) return;

    debugLog(`Polling ${confirmingRewards.size} confirming rewards...`);

    const pollInterval = setInterval(async () => {
      const updates = new Map(confirmingRewards);
      let hasChanges = false;

      for (const [requestId, rewardStatus] of confirmingRewards.entries()) {
        if (rewardStatus.status !== 'confirming') continue;

        try {
          // Find the request to get reward ID
          const request = requests.find(r => r.id === requestId);
          if (!request?.reward?.id) continue;

          const response = await apiClient.rewards.getStatus(request.reward.id);

          if (response.success && response.data) {
            const newStatus = response.data.status;

            if (newStatus !== 'confirming') {
              updates.set(requestId, {
                ...rewardStatus,
                status: newStatus,
                confirmations: response.data.confirmation,
                error: response.data.error,
              });
              hasChanges = true;

              if (newStatus === 'sent') {
                toast.success(`Payment confirmed! ${rewardStatus.coinsEarned.toFixed(2)} HEP sent`);
              } else if (newStatus === 'failed') {
                toast.error(`Payment failed: ${response.data.error || 'Unknown error'}`);
              }

              setTimeout(() => {
                setConfirmingRewards(prev => {
                  const next = new Map(prev);
                  next.delete(requestId);
                  return next;
                });
                fetchRequests();
              }, 3000);

            } else if (response.data.confirmation) {
              const currentConfirmations = response.data.confirmation.current;
              const previousConfirmations = rewardStatus.confirmations?.current || 0;

              if (currentConfirmations !== previousConfirmations) {
                updates.set(requestId, {
                  ...rewardStatus,
                  confirmations: response.data.confirmation,
                });
                hasChanges = true;
              }
            }
          }
        } catch (error: any) {
          errorLog(`Error polling reward for request ${requestId}:`, error);
        }
      }

      if (hasChanges) {
        setConfirmingRewards(updates);
      }
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [confirmingRewards, requests]);

  const fetchRequests = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.payoutRequests.getPending();

      if (response.success && response.data) {
        setRequests(response.data);

        // Pre-populate approvalResults from database
        const results = new Map();
        for (const req of response.data) {
          if (req.approvalMethod) {
            results.set(req.id, {
              autoApproved: req.approvalMethod === 'auto',
              reason: req.approvalReason,
            });
          }
        }
        setApprovalResults(results);
      }
    } catch (error: any) {
      errorLog('Failed to fetch payout requests:', error);
      toast.error('Failed to load payout requests');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectRequest = (requestId: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
      } else {
        newSet.add(requestId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === requests.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(requests.map(r => r.id)));
    }
  };

  const toggleExpand = (requestId: string) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
      } else {
        newSet.add(requestId);
      }
      return newSet;
    });
  };

  const handleApprove = async (requestId: string) => {
    try {
      setProcessingIds(prev => new Set([...prev, requestId]));

      const response = await apiClient.payoutRequests.approve(requestId) as any;

      if (response.success) {
        const autoApproved = response.autoApproved !== false;

        captureEvent('payout_approved', {
          amount_hep: requests.find(r => r.id === requestId)?.totalCoins,
          method: 'single',
          auto_approved: autoApproved,
        });

        setApprovalResults(prev => new Map(prev).set(requestId, {
          autoApproved,
          reason: response.reason,
        }));

        if (autoApproved) {
          toast.success('Payout auto-approved! Transaction submitted.');
        } else {
          toast.success('Payout escalated to admin CLI for signature.');
        }

        // Check if transaction was submitted (auto-approved path)
        if (response.data?.transactionHash) {
          const { transactionHash, coinsDistributed } = response.data;

          toast.success(
            `Transaction submitted: ${transactionHash.slice(0, 10)}... (Confirming...)`,
            { duration: 5000 }
          );

          setConfirmingRewards(prev => new Map(prev).set(requestId, {
            requestId,
            status: 'confirming',
            transactionHash,
            coinsEarned: coinsDistributed,
            confirmations: {
              current: 0,
              required: 3,
              progress: 0,
            },
          }));
        }

        await fetchRequests();
      } else {
        toast.error(response.message || 'Failed to approve payout request');
      }
    } catch (error: any) {
      errorLog('Failed to approve request:', error);
      toast.error(error.message || 'Failed to approve payout request');
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;

    try {
      const idsToApprove = Array.from(selectedIds);
      idsToApprove.forEach(id => setProcessingIds(prev => new Set([...prev, id])));

      const response = await apiClient.payoutRequests.bulkApprove(idsToApprove);

      if (response.success) {
        const { approved, failed } = response.data;

        if (approved.length > 0) {
          captureEvent('payout_approved', {
            method: 'bulk',
            approved_count: approved.length,
            failed_count: failed.length,
          });
          toast.success(`${approved.length} requests auto-approved successfully`);

          // Bulk approve always uses direct blockchain path (auto-approved)
          setApprovalResults(prev => {
            const next = new Map(prev);
            for (const item of approved) {
              next.set(item.id, { autoApproved: true });
            }
            return next;
          });
        }
        if (failed.length > 0) {
          toast.error(`${failed.length} requests failed to approve`);
        }

        setSelectedIds(new Set());
        await fetchRequests();
      }
    } catch (error: any) {
      errorLog('Bulk approve failed:', error);
      toast.error(error.message || 'Bulk approve failed');
    } finally {
      setProcessingIds(new Set());
    }
  };

  const handleReject = async () => {
    if (!rejectingId || rejectionReason.length < 10) {
      toast.error('Rejection reason must be at least 10 characters');
      return;
    }

    try {
      setProcessingIds(prev => new Set([...prev, rejectingId]));

      const response = await apiClient.payoutRequests.reject(rejectingId, rejectionReason);

      if (response.success) {
        captureEvent('payout_rejected', {
          amount_hep: requests.find(r => r.id === rejectingId)?.totalCoins,
        });
        toast.success('Payout request rejected');
        setRejectingId(null);
        setRejectionReason('');
        await fetchRequests();
      } else {
        toast.error(response.message || 'Failed to reject request');
      }
    } catch (error: any) {
      errorLog('Failed to reject request:', error);
      toast.error(error.message || 'Failed to reject request');
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(rejectingId!);
        return newSet;
      });
    }
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const renderPaymentStatus = (requestId: string) => {
    const rewardStatus = confirmingRewards.get(requestId);

    if (!rewardStatus) return null;

    if (rewardStatus.status === 'confirming') {
      const current = rewardStatus.confirmations?.current || 0;
      const required = rewardStatus.confirmations?.required || 3;
      const progress = rewardStatus.confirmations?.progress || 0;

      return (
        <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
            <span className="text-sm font-medium text-blue-900">
              Confirming Payment...
            </span>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-blue-700">
              <span>Confirmations: {current}/{required}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex items-center gap-1 text-xs text-blue-600">
              <ExternalLink className="h-3 w-3" />
              <Link
                to={`/explorer?hash=${rewardStatus.transactionHash}`}
                className="hover:underline"
              >
                {rewardStatus.transactionHash.slice(0, 10)}...{rewardStatus.transactionHash.slice(-8)}
              </Link>
            </div>
          </div>
        </div>
      );
    }

    if (rewardStatus.status === 'sent') {
      return (
        <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <Check className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-green-900">
              Payment Confirmed!
            </span>
          </div>
          <div className="mt-1 text-xs text-green-700">
            {rewardStatus.coinsEarned.toFixed(2)} HEP sent to wallet
          </div>
        </div>
      );
    }

    if (rewardStatus.status === 'failed') {
      return (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <X className="h-4 w-4 text-red-600" />
            <span className="text-sm font-medium text-red-900">
              Payment Failed
            </span>
          </div>
          <div className="mt-1 text-xs text-red-700">
            {rewardStatus.error || 'Transaction failed to confirm'}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin')}
            className="p-2 text-gray-500 hover:text-gray-700 touch-manipulation"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-4xl font-bold text-white">Payout Management</h1>
            <p className="text-lg text-white mt-1">Review and approve payout requests</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/admin/payouts/history')}
          className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg font-medium transition-colors"
        >
          <Clock size={18} />
          <span className="hidden sm:inline">View History</span>
        </button>
      </div>

      {/* Bulk Actions Bar */}
      {requests.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="selectAll"
                  checked={selectedIds.size === requests.length && requests.length > 0}
                  onChange={handleSelectAll}
                  className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="selectAll" className="text-sm font-medium text-gray-700">
                  Select All ({requests.length})
                </label>
              </div>
              {selectedIds.size > 0 && (
                <span className="text-sm text-gray-600">
                  {selectedIds.size} selected
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleBulkApprove}
                disabled={selectedIds.size === 0 || processingIds.size > 0}
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <CheckCircle size={18} />
                Approve Selected ({selectedIds.size})
              </button>
              <button
                onClick={fetchRequests}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium px-4 py-2"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Requests List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading payout requests...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <DollarSign size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500 text-lg mb-2">No pending payout requests</p>
            <p className="text-sm text-gray-400">
              All payout requests have been processed.
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {requests.map((request) => (
              <div
                key={request.id}
                className={`bg-white rounded-lg border-2 p-4 sm:p-6 transition-colors ${
                  selectedIds.has(request.id)
                    ? 'border-blue-500 ring-2 ring-blue-200'
                    : 'border-yellow-200 bg-yellow-50 hover:border-yellow-300'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  {/* Checkbox + Request Info */}
                  <div className="flex items-start gap-3 flex-1">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(request.id)}
                      onChange={() => handleSelectRequest(request.id)}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
                    />

                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className="font-medium text-gray-900">
                            {request.user?.fullName || 'Unknown User'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600">
                            Requested {format(parseISO(request.createdAt), 'MMM d, yyyy h:mm a')}
                          </span>
                        </div>
                        {approvalResults.has(request.id) && (
                          approvalResults.get(request.id)!.autoApproved ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <Zap className="h-3 w-3" />
                              Auto-Approved
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                              <PenTool className="h-3 w-3" />
                              Awaiting Signature
                            </span>
                          )
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-sm">
                        <div>
                          <span className="text-gray-500 text-xs sm:text-sm">Email:</span>
                          <p className="font-medium truncate" title={request.user?.email}>
                            {request.user?.email || '-'}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500 text-xs sm:text-sm">Total Hours:</span>
                          <p className="font-medium text-blue-600">{request.totalHours.toFixed(2)}h</p>
                        </div>
                        <div>
                          <span className="text-gray-500 text-xs sm:text-sm">Payout Amount:</span>
                          <p className="font-medium text-green-600">{request.totalCoins.toFixed(2)} HEP</p>
                        </div>
                        <div>
                          <span className="text-gray-500 text-xs sm:text-sm">Wallet:</span>
                          <p className="font-mono text-xs sm:text-sm truncate" title={request.user?.walletAddress}>
                            {request.user?.walletAddress
                              ? formatAddress(request.user.walletAddress)
                              : 'Not set'}
                          </p>
                        </div>
                      </div>

                      {/* Expandable Sessions */}
                      {request.sessions && request.sessions.length > 0 && (
                        <div className="mt-2">
                          <button
                            onClick={() => toggleExpand(request.id)}
                            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                          >
                            {expandedIds.has(request.id) ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                            {request.sessions.length} sessions included
                          </button>

                          {expandedIds.has(request.id) && (
                            <div className="mt-2 bg-gray-50 rounded-lg p-3 space-y-2">
                              {request.sessions.map((session) => (
                                <div key={session.id} className="flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-2">
                                    <Clock className="h-3 w-3 text-gray-400" />
                                    <span>{format(parseISO(session.date), 'MMM d')}</span>
                                    <span className="text-gray-500">
                                      {session.startTime} - {session.endTime}
                                    </span>
                                  </div>
                                  <span className="text-blue-600 font-medium">
                                    {formatDuration(session.durationMinutes)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Payment Status */}
                      {renderPaymentStatus(request.id)}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleApprove(request.id)}
                      disabled={processingIds.has(request.id) || !request.user?.walletAddress}
                      className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      <CheckCircle size={16} />
                      {processingIds.has(request.id) ? 'Processing...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => {
                        setRejectingId(request.id);
                        setRejectionReason('');
                      }}
                      disabled={processingIds.has(request.id)}
                      className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
                    >
                      <XCircle size={16} />
                      Reject
                    </button>
                  </div>
                </div>

                {!request.user?.walletAddress && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-700">
                      User has no wallet address configured. Cannot process payment.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary */}
      {requests.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">Summary</h3>
          <div className="text-sm text-blue-800 space-y-1">
            <p>Total pending requests: {requests.length}</p>
            <p>Total hours: {requests.reduce((acc, r) => acc + r.totalHours, 0).toFixed(2)}</p>
            <p>Total payout amount: <span className="font-semibold">{requests.reduce((acc, r) => acc + r.totalCoins, 0).toFixed(2)} HEP</span></p>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {rejectingId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Reject Payout Request</h2>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600">
                Please provide a reason for rejecting this payout request.
                The user will be notified and their sessions will be returned to eligible status.
              </p>
              <div>
                <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason (min 10 characters)
                </label>
                <textarea
                  id="reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Please provide a detailed reason for rejection..."
                />
                <p className="mt-1 text-xs text-gray-500">
                  {rejectionReason.length}/10 characters minimum
                </p>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setRejectingId(null);
                    setRejectionReason('');
                  }}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={rejectionReason.length < 10 || processingIds.has(rejectingId)}
                  className="flex-1 px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {processingIds.has(rejectingId) ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutManagementPage;
